/**
 * ThermometerProgressBar — striped, glossy "thermometer" fill bar.
 *
 * Web port of client-native/src/ui/molecules/ThermometerProgressBar.tsx
 * (iOS source of truth). The iOS version draws 30 rotated 6px stripe Views
 * spaced every 14px at -45deg; here that's reproduced faithfully with a
 * repeating-linear-gradient. Matches exact metrics: height 18, radius
 * height/2, default track --thermo-track, fill --color-blue, stripes
 * --thermo-stripe, glossy top highlight --thermo-gloss @ 34% opacity.
 * With `shouldAnimate` (default true) the fill animates from 0 → progress
 * over 1200ms ease-out after a 300ms delay on mount, then fires
 * `onFillComplete`; otherwise it renders at progress immediately.
 */
import { useEffect, useState, type CSSProperties } from "react";

export interface ThermometerProgressBarProps {
  /** Progress value between 0 and 1 (clamped). */
  progress: number;
  /** When true, animates the fill from 0 → progress on mount. */
  shouldAnimate?: boolean;
  fillColor?: string;
  trackColor?: string;
  stripeColor?: string;
  /** Bar height in px (default 18, matching iOS). */
  height?: number;
  style?: CSSProperties;
  /**
   * Fired once the fill animation reaches its target.
   * Only invoked when `shouldAnimate` is true.
   */
  onFillComplete?: () => void;
}

const DEFAULT_HEIGHT = 18;
export const THERMO_FILL_DURATION = 1200;
export const THERMO_FILL_START_DELAY = 300;

export default function ThermometerProgressBar({
  progress,
  shouldAnimate = true,
  fillColor = "var(--color-blue)",
  trackColor = "var(--thermo-track)",
  stripeColor = "var(--thermo-stripe)",
  height = DEFAULT_HEIGHT,
  style,
  onFillComplete,
}: ThermometerProgressBarProps) {
  const clamped = Math.max(0, Math.min(1, progress));

  // Start empty, then fill to target after the start delay so the CSS
  // transition runs (mirrors reanimated's withDelay + withTiming on mount).
  const [fill, setFill] = useState(shouldAnimate ? 0 : clamped);

  useEffect(() => {
    if (!shouldAnimate) {
      setFill(clamped);
      return;
    }
    const startTimer = setTimeout(
      () => setFill(clamped),
      THERMO_FILL_START_DELAY,
    );
    const completeTimer = onFillComplete
      ? setTimeout(
          onFillComplete,
          THERMO_FILL_START_DELAY + THERMO_FILL_DURATION,
        )
      : undefined;
    return () => {
      clearTimeout(startTimer);
      if (completeTimer) clearTimeout(completeTimer);
    };
  }, [clamped, shouldAnimate, onFillComplete]);

  const radius = height / 2;
  const highlightHeight = Math.max(1, Math.round(height * 0.18));

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height,
          borderRadius: radius,
          backgroundColor: trackColor,
          overflow: "hidden",
        }}
      >
        {/* Diagonal stripes across the full track. */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `repeating-linear-gradient(-45deg, ${stripeColor} 0 6px, transparent 6px 14px)`,
          }}
        />
        {/* Animated fill. */}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: `${Math.max(fill * 100, 0)}%`,
            minWidth: height,
            borderRadius: radius,
            backgroundColor: fillColor,
            overflow: "hidden",
            transition: shouldAnimate
              ? `width ${THERMO_FILL_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1)`
              : undefined,
          }}
        >
          {/* Glossy top highlight strip. */}
          <div
            style={{
              position: "absolute",
              left: 8,
              right: 8,
              top: 4,
              height: highlightHeight,
              borderRadius: "var(--radius-full)",
              opacity: 0.34,
              backgroundColor: "var(--thermo-gloss)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
